
export function getClusterIds(timeseries: any[]): number[] {
    return (timeseries[0]?.data?.clusters ?? []).map(
        (c: { cluster_id: number }) => c.cluster_id
    );
}

export interface ClusterReturnSeries {
    asset: string;
    firstPrice: number;
    timeseriesData: Array<{
        timestamp: string;
        price: number;
        activeClusterId: number | undefined;
        clusterReturns: Record<number, number>;
        clusterFeatures: Record<number, Record<string, number>>;
        clusterMetrics: Record<number, Record<string, number>>;
    }>;
}

export function computeClusterReturns(
    timeseries: any[],
    clusterIds: number[],
    strategy: string
): ClusterReturnSeries {
    const timeseriesData: ClusterReturnSeries['timeseriesData'] = [];
    let asset = '';
    let firstPrice = NaN;

    for (let i = 0; i < timeseries.length; i++) {
        const { timestamp, data } = timeseries[i];
        const symbol = data?.price?.symbol ?? '';
        const price = parseFloat(data?.price?.close ?? '0');

        if (!isNaN(price) && i === 0) {
            firstPrice = price;
        }

        if (i === 0) {
            asset = symbol;
        }

        const rankMap = data?.cluster_assignment?.[strategy] as Record<string, number> | undefined;

        // Fixed: directly access the rankMap using cluster_id as key
        const rank = data?.cluster_id;
        const activeClusterId = rankMap && typeof rank === 'number'
            ? rankMap[rank] ?? rankMap[rank.toString()]
            : undefined;

        const clusterReturns: Record<number, number> = Object.fromEntries(
            clusterIds.map(id => [id, 0])
        );

        // Extract and map cluster metrics
        const clusterMetrics: Record<number, Record<string, number>> = {};
        if (data?.clusters && rankMap) {
            // Iterate through the cluster_feature_means map
            Object.entries(data.clusters).forEach(([originalClusterId, metrics]) => {
                const originalId = parseInt(originalClusterId);
                // Map the original cluster ID to the ranked cluster ID
                const mappedClusterId = rankMap[originalId] ?? rankMap[originalId.toString()];

                if (mappedClusterId !== undefined &&
                    !isNaN(mappedClusterId) &&
                    clusterIds.includes(mappedClusterId)) {
                    clusterMetrics[mappedClusterId] = metrics as Record<string, number>;
                }
            });
        }

        // Extract and map cluster features
        const clusterFeatures: Record<number, Record<string, number>> = {};
        if (data?.cluster_feature_means && rankMap) {
            // Iterate through the cluster_feature_means map
            Object.entries(data.cluster_feature_means).forEach(([originalClusterId, features]) => {
                const originalId = parseInt(originalClusterId);
                // Map the original cluster ID to the ranked cluster ID
                const mappedClusterId = rankMap[originalId] ?? rankMap[originalId.toString()];

                if (mappedClusterId !== undefined &&
                    !isNaN(mappedClusterId) &&
                    clusterIds.includes(mappedClusterId)) {
                    clusterFeatures[mappedClusterId] = features as Record<string, number>;
                }
            });
        }

        if (i > 0) {
            const prevData = timeseries[i - 1]?.data;
            const closePrev = parseFloat(prevData?.price?.close ?? '0');
            const closeCurr = parseFloat(data?.price?.close ?? '0');
            const prevClusterIdx = prevData?.cluster_id;

            if (typeof prevClusterIdx === 'number' && closePrev > 0 && closeCurr > 0) {
                const prevRankMap = prevData?.cluster_assignment?.[strategy] as Record<string, number> | undefined;

                if (prevRankMap) {
                    // Fixed: directly access the previous rankMap using cluster_id as key
                    const actualClusterId = prevRankMap[prevClusterIdx] ?? prevRankMap[prevClusterIdx.toString()];

                    if (
                        actualClusterId !== undefined &&
                        !isNaN(actualClusterId) &&
                        clusterIds.includes(actualClusterId)
                    ) {
                        const r = (closeCurr / closePrev) - 1;
                        clusterReturns[actualClusterId] = r;
                    }
                }
            }
        }

        timeseriesData.push({
            timestamp,
            price,
            activeClusterId: activeClusterId !== undefined && activeClusterId !== -1 ? activeClusterId : undefined,
            clusterReturns,
            clusterFeatures,
            clusterMetrics
        });
    }

    return {
        asset,
        firstPrice,
        timeseriesData,
    };
}


// Updated computeCumulativeReturns function
export const computeCumulativeReturns = (
    timeseriesData: ClusterReturnSeries['timeseriesData'],
    clusterIds: number[],
    allocations: Record<number, number>,
    enabledClusters: Record<number, boolean>,
    metricRules?: MetricRuleSet,
    turnoverCost: number = 0,
): { individual: Record<number, number>[], combined: number[] } => {
    const cumulativeReturns: Record<number, number> = Object.fromEntries(
        clusterIds.map(id => [id, 1.0])
    );
    let combinedCumulative = 1.0;
    const individualReturns: Record<number, number>[] = [];
    const combinedReturns: number[] = [];
    // NEW
    const EPS = 1e-6;
    let prevEffectiveAlloc = 0; // allocation used last period for combined leg (0 = flat)

    timeseriesData.forEach((data, i) => {
        // Individual cluster returns with metric rules
        if (i === 0) {
            // First period: no returns, just push initial cumulative values
            individualReturns.push({ ...cumulativeReturns });
        } else {
            const prevData = timeseriesData[i - 1];
            for (const clusterId of clusterIds) {
                let multiplier = 1.0; // Default: no return (stay flat)

                // Only invest if this cluster was active in the previous period
                if (prevData.activeClusterId === clusterId) {
                    const r = data.clusterReturns[clusterId] || 0;
                    let allocation = allocations[clusterId] ?? 1;

                    // Apply metric rules using PREVIOUS period's data
                    if (metricRules?.groups.length != undefined && metricRules?.groups.length > 0) {
                        const ruleResult = evaluateRuleSet(metricRules, prevData.clusterFeatures, prevData.clusterMetrics, clusterId);
                        if (ruleResult.applied) {
                            allocation = ruleResult.factor;
                        }
                    }

                    if (allocation >= 0) {
                        multiplier = 1 + r * allocation;
                    } else {
                        const shortFactor = 1 / (1 + r);
                        multiplier = 1 + (shortFactor - 1) * Math.abs(allocation);
                    }
                }

                cumulativeReturns[clusterId] = Math.max(0, cumulativeReturns[clusterId] * multiplier);
            }
            individualReturns.push({ ...cumulativeReturns });
        }

        // NEW: track the allocation actually used this period (0 if not included)
        let allocationUsed = 0;

        // Combined return calculation with metric rules
        let periodReturn = 0;
        if (i > 0) {
            const prevData = timeseriesData[i - 1];
            const hasEnabledClusters = Object.values(enabledClusters).some(enabled => enabled);
            const hasMetricRules = metricRules && metricRules.groups.length > 0;

            if (prevData.activeClusterId !== undefined) {
                const shouldInclude = hasEnabledClusters
                    ? enabledClusters[prevData.activeClusterId]
                    : hasMetricRules;

                if (shouldInclude) {
                    const r = data.clusterReturns[prevData.activeClusterId] || 0;
                    let allocation = allocations[prevData.activeClusterId] ?? 1;

                    // Apply metric rules using previous period's data
                    if (metricRules) {
                        const ruleResult = evaluateRuleSet(metricRules, prevData.clusterFeatures, prevData.clusterMetrics, prevData.activeClusterId);
                        if (ruleResult.applied) {
                            allocation = ruleResult.factor;
                        }
                    }

                    // remember effective allocation used for this period
                    allocationUsed = allocation;

                    if (allocation >= 0) {
                        periodReturn = r * allocation;
                    } else {
                        const shortFactor = 1 / (1 + r);
                        periodReturn = (shortFactor - 1) * Math.abs(allocation);
                    }
                }
            }
        }

        // build multiplier
        let multiplier = 1 + periodReturn;
        const delta = Math.min(1, Math.abs(allocationUsed - prevEffectiveAlloc));
        if (turnoverCost > 0 && delta > EPS) {
            multiplier *= Math.max(0, 1 - turnoverCost * delta);
        }

        combinedCumulative *= multiplier;
        combinedCumulative = Math.max(0, combinedCumulative);
        combinedReturns.push(combinedCumulative);

        // NEW: Zustand für nächste Periode merken
        prevEffectiveAlloc = allocationUsed;
    });

    return {
        individual: individualReturns,
        combined: combinedReturns
    };
};

export function computeDomainsFromCumulativeReturns(
    prices: number[],
    individualReturns: Record<number, number>[],
    combinedReturns: number[],
    firstPrice: number
): {
    priceDomain: [number, number];
    returnDomain: [number, number];
    logPriceDomain: [number, number];
    logReturnDomain: [number, number];
} {
    const priceOrigin = firstPrice;
    const returnOrigin = 1.0;

    const allReturnValues = [
        ...individualReturns.flatMap(r => Object.values(r)),
        ...combinedReturns
    ];

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const minReturn = Math.min(...allReturnValues);
    const maxReturn = Math.max(...allReturnValues);

    // LINEAR SCALE DOMAINS (your existing logic)
    const priceCenter = (minPrice + maxPrice) / 2;
    const returnCenter = (minReturn + maxReturn) / 2;

    const priceShift = (priceCenter - priceOrigin) / priceOrigin;
    const returnShift = (returnCenter - returnOrigin) / returnOrigin;
    const avgShift = (priceShift + returnShift) / 2;

    const priceRange = Math.max(
        Math.abs(minPrice - priceOrigin),
        Math.abs(maxPrice - priceOrigin)
    ) / priceOrigin;
    const returnRange = Math.max(
        Math.abs(minReturn - returnOrigin),
        Math.abs(maxReturn - returnOrigin)
    ) / returnOrigin;

    const maxRange = Math.max(priceRange, returnRange);
    const paddedRange = maxRange * 1.05;

    const shiftedPriceCenter = priceOrigin * (1 + avgShift);
    const shiftedReturnCenter = returnOrigin * (1 + avgShift);

    const priceLower = Math.max(0, shiftedPriceCenter - (priceOrigin * paddedRange));
    const returnLower = Math.max(0, shiftedReturnCenter - (returnOrigin * paddedRange));

    // LOG SCALE DOMAINS (asymmetric, data-driven)
    const safeMinPrice = Math.max(0.001, minPrice);
    const safeMinReturn = Math.max(0.001, minReturn);

    // Calculate actual ratios from origins to data extremes
    const priceRatioDown = priceOrigin / safeMinPrice;
    const priceRatioUp = maxPrice / priceOrigin;
    const returnRatioDown = returnOrigin / safeMinReturn;
    const returnRatioUp = maxReturn / returnOrigin;

    // Find the maximum ratio needed for each direction
    const maxDownRatio = Math.max(priceRatioDown, returnRatioDown);
    const maxUpRatio = Math.max(priceRatioUp, returnRatioUp);

    // Add padding
    const paddedDownRatio = maxDownRatio * 1.1;
    const paddedUpRatio = maxUpRatio * 1.1;

    return {
        priceDomain: [
            priceLower,
            shiftedPriceCenter + (priceOrigin * paddedRange)
        ],
        returnDomain: [
            returnLower,
            shiftedReturnCenter + (returnOrigin * paddedRange)
        ],
        logPriceDomain: [
            Math.max(0.001, priceOrigin / paddedDownRatio),
            priceOrigin * paddedUpRatio
        ],
        logReturnDomain: [
            Math.max(0.001, returnOrigin / paddedDownRatio),
            returnOrigin * paddedUpRatio
        ]
    };
}

export type ClusterMetricMap = Record<string, number>;

export function calculateClusterMetrics(returns: number[], riskFreeRate = 0): ClusterMetricMap {
    const safe = (x: number) => (isFinite(x) ? x : 0);
    const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
    const std = (arr: number[], ddof = 0) => {
        const avg = mean(arr);
        const variance = arr.reduce((sum, r) => sum + Math.pow(r - avg, 2), 0) / (arr.length - ddof);
        return Math.sqrt(variance);
    };

    // Helper functions to match Python implementation
    const lpm = (returns: number[], threshold: number, order: number) => {
        const diff = returns.map(r => Math.max(0, threshold - r));
        return diff.reduce((sum, d) => sum + Math.pow(d, order), 0) / Math.max(1, returns.length);
    };

    const hpm = (returns: number[], threshold: number, order: number) => {
        const diff = returns.map(r => Math.max(0, r - threshold));
        return diff.reduce((sum, d) => sum + Math.pow(d, order), 0) / Math.max(1, returns.length);
    };

    const totalReturn = returns.reduce((acc, r) => acc * (1 + r), 1);
    const volatility = std(returns);

    const maxDrawdown = (() => {
        if (returns.length <= 1) return 0.0;
        const values = returns.reduce((acc, r) => {
            acc.push(acc[acc.length - 1] * (1 + r));
            return acc;
        }, [1]);

        let maxDD = 0;
        let peak = values[0];

        for (let i = 1; i < values.length; i++) {
            if (values[i] > peak) {
                peak = values[i];
            }
            const drawdown = Math.abs((values[i] / peak) - 1);
            maxDD = Math.max(maxDD, drawdown);
        }

        return maxDD;
    })();

    // Fixed CVaR calculation
    const cvar = (() => {
        const sorted = [...returns].sort((a, b) => a - b);
        const alpha = 0.05;
        const idx = Math.floor(alpha * sorted.length);

        if (idx === 0 || sorted.length === 0) return 0; // Return 0 instead of NaN

        const tail = sorted.slice(0, idx);
        const result = Math.abs(tail.reduce((a, b) => a + b, 0) / idx);
        return isFinite(result) ? result : 0; // Additional safety check
    })();


    const er = mean(returns);
    const excessReturn = er - riskFreeRate;

    // Fixed Sortino ratio - using LPM with threshold=0, order=2
    const downsideDeviation = Math.sqrt(lpm(returns, 0, 2));

    // Fixed Omega ratio - using HPM/LPM with threshold=riskFreeRate (not 0)
    const lpm1 = lpm(returns, riskFreeRate, 1);
    const hpm1 = hpm(returns, riskFreeRate, 1);

    // Simple gain/loss ratio - sum of positive returns / sum of absolute negative returns
    const gainLossRatio = (() => {
        const gains = returns.filter(r => r > 0).reduce((sum, r) => sum + r, 0);
        const losses = returns.filter(r => r < 0).reduce((sum, r) => sum + Math.abs(r), 0);
        return safe(gains / Math.max(1e-6, losses));
    })();

    return {
        total_return: totalReturn,
        volatility: safe(volatility),
        conditional_sharpe_ratio: safe(cvar > 0 ? excessReturn / cvar : 0),
        conditional_value_at_risk: cvar,
        max_drawdown: maxDrawdown,
        sharpe_ratio: safe(excessReturn / Math.max(1e-6, volatility)),
        omega_ratio: safe(hpm1 / Math.max(1e-6, lpm1)), // Fixed: should be hpm/lpm, not excessReturn/lpm
        sortino_ratio: safe(excessReturn / Math.max(1e-6, downsideDeviation)),
        gain_loss_ratio: gainLossRatio, // Fixed: simple positive/negative ratio
        upside_potential_ratio: safe(hpm(returns, 0, 1) / Math.max(1e-6, Math.sqrt(lpm(returns, 0, 2)))),
    };
}

import { ClusterFeatureSeries, FeatureTimeseriesData } from './types';

// Extract cluster features over time
export function computeClusterFeatures(
    timeseries: any[],
    clusterIds: number[],
    strategy: string,
): ClusterFeatureSeries {
    const timeseriesData: FeatureTimeseriesData[] = [];
    const allFeatureNamesSet = new Set<string>();
    let asset = '';

    for (let i = 0; i < timeseries.length; i++) {
        const { timestamp, data } = timeseries[i];

        if (i === 0) {
            asset = data?.price?.symbol ?? '';
        }

        const rank = data?.cluster_id;
        const rankMap = data?.cluster_assignment?.[strategy] as Record<string, number> | undefined;
        const activeClusterId = rankMap && typeof rank === 'number'
            ? rankMap[rank] ?? rankMap[rank.toString()]
            : undefined;

        const clusterFeatures: Record<number, Record<string, number>> = {};

        if (data?.cluster_feature_means && rankMap) {
            Object.entries(data.cluster_feature_means).forEach(([originalClusterId, features]) => {
                const originalId = parseInt(originalClusterId);
                const mappedClusterId = rankMap[originalId] ?? rankMap[originalId.toString()];

                if (mappedClusterId !== undefined &&
                    !isNaN(mappedClusterId) &&
                    clusterIds.includes(mappedClusterId)) {
                    const featureObj = features as Record<string, number>;
                    clusterFeatures[mappedClusterId] = featureObj;

                    // Collect all feature names
                    Object.keys(featureObj).forEach(featureName => {
                        if (featureName !== 'cluster_id') {
                            allFeatureNamesSet.add(featureName);
                        }
                    });
                }
            });
        }

        timeseriesData.push({
            timestamp,
            clusterFeatures,
            activeClusterId: activeClusterId !== undefined && activeClusterId !== -1 ? activeClusterId : undefined,
        });
    }

    return {
        asset,
        timeseriesData,
        allFeatureNames: Array.from(allFeatureNamesSet).sort(),
    };
}

// Types for metric rules
export interface MetricRule {
    id: string;
    field: string;
    operator: 'gt' | 'lt' | 'gte' | 'lte' | 'eq' | 'neq';
    threshold: number;
    allocationFactor: number;
    enabled: boolean;
}

export interface RuleGroup {
    id: string;
    rules: MetricRule[];
    logic: 'AND' | 'OR';
}

export interface MetricRuleSet {
    groups: RuleGroup[];
    groupLogic: 'AND' | 'OR';
}

// Helper to extract all available fields from data
export const extractAvailableFields = (timeseriesData: any[]): string[] => {
    if (timeseriesData.length === 0) return [];

    const lastData = timeseriesData[timeseriesData.length - 1]?.data;
    if (!lastData) return [];

    const fields = new Set<string>();


    // Add cluster-specific fields from clusters map
    if (lastData.clusters) {
        Object.values(lastData.clusters).forEach((cluster: any) => {
            if (cluster) {
                Object.keys(cluster).forEach(key => fields.add(`metrics.${key}`));
            }
        });
    }

    // Add feature_means fields
    if (lastData.cluster_feature_means) {
        Object.keys(lastData.cluster_feature_means).forEach(clusterId => {
            if (lastData.cluster_feature_means[clusterId]) {
                Object.keys(lastData.cluster_feature_means[clusterId]).forEach(key =>
                    fields.add(`features.${key}`)
                );
            }
        });
    }


    return Array.from(fields).sort();
};

const getFieldValue = (
    clusterFeatures: Record<number, Record<string, number>>,
    clusterMetrics: Record<number, Record<string, number>>,
    field: string,
    clusterId: number
): number | null => {
    const [category, key] = field.split('.');

    const sourceMap =
        category === 'features' ? clusterFeatures :
            category === 'metrics' ? clusterMetrics :
                null;

    return sourceMap?.[clusterId]?.[key] ?? null;
};


// Helper to evaluate a single rule
const evaluateRule = (
    rule: MetricRule,
    clusterFeatures: Record<number, Record<string, number>>,
    clusterMetrics: Record<number, Record<string, number>>,
    clusterId: number
): boolean => {
    const value = getFieldValue(clusterFeatures, clusterMetrics, rule.field, clusterId);
    if (value === null) return false;

    if (!rule.enabled) return true;

    switch (rule.operator) {
        case 'gt': return value > rule.threshold;
        case 'lt': return value < rule.threshold;
        case 'gte': return value >= rule.threshold;
        case 'lte': return value <= rule.threshold;
        case 'eq': return value === rule.threshold;
        case 'neq': return value !== rule.threshold;
        default: return false;
    }
};

interface RuleEvaluationResult {
    applied: boolean;
    factor: number;
}

const evaluateRuleSet = (
    ruleSet: MetricRuleSet,
    clusterFeatures: Record<number, Record<string, number>>,
    clusterMetrics: Record<number, Record<string, number>>,
    clusterId: number
): RuleEvaluationResult => {
    if (ruleSet.groups.length === 0) {
        return { applied: false, factor: 1 };
    }

    const groupResults = ruleSet.groups.map(group => {
        if (group.rules.length === 0) {
            return { applied: false, factor: 1 };
        }

        const ruleResults = group.rules.map(rule => {
            const passed = evaluateRule(rule, clusterFeatures, clusterMetrics, clusterId);
            return {
                passed,
                factor: rule.allocationFactor,
                enabled: rule.enabled !== false
            };
        });

        if (group.logic === 'AND') {
            const allPassed = ruleResults.every(r => r.passed);
            const allEnabled = ruleResults.every(r => r.enabled);

            if (allPassed) {
                const minFactor = Math.min(...ruleResults.map(r => r.factor));
                return { applied: allEnabled, factor: minFactor };
            } else {
                const minFactor = Math.min(...ruleResults.map(r => r.factor));
                return { applied: false, factor: minFactor };
            }
        } else { // OR logic
            const passedRule = ruleResults.find(r => r.passed);
            if (passedRule) {
                return { applied: passedRule.enabled, factor: passedRule.factor };
            } else {
                return { applied: false, factor: ruleResults[0]?.factor ?? 1 };
            }
        }
    });

    if (ruleSet.groupLogic === 'AND') {
        const allApplied = groupResults.every(result => result.applied);
        if (allApplied) {
            const minFactor = Math.min(...groupResults.map(r => r.factor));
            return { applied: true, factor: minFactor };
        } else {
            const minFactor = Math.min(...groupResults.map(r => r.factor));
            return { applied: false, factor: minFactor };
        }
    } else { // OR logic
        const appliedGroup = groupResults.find(result => result.applied);
        if (appliedGroup) {
            return { applied: true, factor: appliedGroup.factor };
        } else {
            const firstFactor = groupResults[0]?.factor ?? 1;
            return { applied: false, factor: firstFactor };
        }
    }
};